(() => {
  class VelvetCollection extends HTMLElement {
    connectedCallback() {
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.classList.add('is-enhanced');
      this.panel = this.querySelector('.collection-filters');
      this.mobile = matchMedia('(max-width: 920px)');
      this.syncPanel();
      this.mobile.addEventListener('change', () => { this.closeFilters(false); this.syncPanel(); }, options);
      this.addEventListener('click', event => this.click(event), options);
      this.addEventListener('submit', event => {
        if (event.target.matches('[data-filter-form]')) { event.preventDefault(); this.filter(); }
      }, options);
      this.addEventListener('change', event => {
        const input = event.target;
        if (input.matches('[data-sort]') || (input.form?.matches('[data-filter-form]') && !this.mobile.matches)) this.filter();
        if (input.matches('[data-price-range]') && !this.mobile.matches) this.filter();
      }, options);
      this.addEventListener('input', event => {
        if (event.target.matches('[data-price-min],[data-price-max]')) {
          window.clearTimeout(this.inputTimer);
          if (!this.mobile.matches) this.inputTimer = window.setTimeout(() => this.filter(), 450);
          return;
        }
        if (!event.target.matches('[data-price-range]')) return;
        const group = event.target.closest('.price-filter');
        group.querySelector('[data-price-max]').value = event.target.value;
        group.querySelector('[data-price-output]').textContent = window.VelvetMoney ? window.VelvetMoney.format(Math.round(Number(event.target.value) * 100)) : new Intl.NumberFormat(document.documentElement.lang, {
          style: 'currency', currency: this.dataset.currency || document.documentElement.dataset.currency
        }).format(Number(event.target.value));
      }, options);
      this.addEventListener('keydown', event => {
        if (!this.panel?.classList.contains('open')) return;
        if (event.key === 'Escape') { event.preventDefault(); this.closeFilters(); }
        if (event.key === 'Tab') {
          const items = [...this.panel.querySelectorAll('a,button,input,summary,select,[tabindex="0"]')].filter(el => !el.disabled && el.getClientRects().length);
          const first = items[0], last = items.at(-1);
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
          if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        }
      }, options);
      window.addEventListener('popstate', () => this.navigate(location.href, false), options);
      document.addEventListener('shopify:block:select', event => {
        if (this.panel?.contains(event.target) && this.mobile.matches) this.openFilters();
      }, options);
    }
    disconnectedCallback() {
      this.events?.abort(); this.request?.abort(); window.clearTimeout(this.inputTimer); this.closeFilters(false);
    }
    syncPanel() {
      if (this.panel) this.panel.inert = this.mobile.matches && !this.panel.classList.contains('open');
    }
    openFilters() {
      this.panel.classList.add('open'); this.panel.inert = false;
      this.panel.setAttribute('role', 'dialog'); this.panel.setAttribute('aria-modal', 'true');
      this.querySelector('.filter-overlay').hidden = false;
      this.querySelector('.filter-open').setAttribute('aria-expanded', 'true');
      this.savedOverflow = document.body.style.overflow; document.body.style.overflow = 'hidden'; this.locked = true;
      this.panel.querySelector('.filter-close').focus();
    }
    closeFilters(restoreFocus = true) {
      if (this.locked) { document.body.style.overflow = this.savedOverflow; this.locked = false; }
      this.panel?.classList.remove('open'); this.panel?.removeAttribute('role'); this.panel?.removeAttribute('aria-modal');
      const overlay = this.querySelector('.filter-overlay'); if (overlay) overlay.hidden = true;
      const opener = this.querySelector('.filter-open'); opener?.setAttribute('aria-expanded', 'false');
      this.syncPanel(); if (restoreFocus) opener?.focus();
    }
    click(event) {
      const target = event.target.closest('button,a,.filter-overlay'); if (!target) return;
      if (target.matches('.filter-open')) this.openFilters();
      if (target.matches('.filter-close,.filter-overlay')) this.closeFilters();
      if (target.matches('[data-view]')) {
        this.querySelector('[data-product-grid]').classList.toggle('compact', target.dataset.view === 'compact');
        this.querySelectorAll('[data-view]').forEach(button => {
          button.classList.toggle('active', button === target); button.setAttribute('aria-pressed', String(button === target));
        });
      }
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (target.matches('[data-filter-link]')) { event.preventDefault(); this.navigate(target.href); }
      if (target.matches('[data-load-more]')) { event.preventDefault(); this.loadMore(target); }
    }
    filter() {
      window.clearTimeout(this.inputTimer);
      const form = this.querySelector('[data-filter-form]'); if (!form.reportValidity()) return;
      const url = new URL(location.href); url.pathname = new URL(form.action).pathname;
      for (const key of [...url.searchParams.keys()]) if (key.startsWith('filter.') || key === 'page' || key === 'sort_by') url.searchParams.delete(key);
      if (form.querySelector('[name="q"]')) {
        url.searchParams.delete('q');
        url.searchParams.delete('type');
        url.searchParams.delete('options');
        url.searchParams.delete('options[prefix]');
      }
      for (const [key, value] of new FormData(form)) if (value !== '') url.searchParams.append(key, value);
      this.navigate(url.href);
    }
    async fetchSection(url) {
      this.request?.abort(); this.request = new AbortController();
      const response = await fetch(url, { signal: this.request.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Request failed');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const section = document.getElementById(this.id);
      if (!section?.matches('velvet-collection')) throw new Error('Collection missing');
      return section;
    }
    async navigate(url, push = true) {
      this.closeFilters(false); this.setAttribute('aria-busy', 'true');
      this.querySelector('[data-status]').textContent = this.dataset.loading;
      const activeId = document.activeElement?.id;
      const compact = this.querySelector('[data-product-grid]').classList.contains('compact');
      const openGroups = [...this.querySelectorAll('details')].map(el => el.open);
      try {
        const next = await this.fetchSection(url);
        if (compact) { next.querySelector('[data-product-grid]').classList.add('compact'); next.querySelectorAll('[data-view]').forEach(button => { const selected = button.dataset.view === 'compact'; button.classList.toggle('active', selected); button.setAttribute('aria-pressed', String(selected)); }); }
        next.querySelectorAll('details').forEach((el, i) => { if (openGroups[i] !== undefined) el.open = openGroups[i]; });
        if (push) history.pushState({}, '', url);
        this.replaceWith(next);
        const focus = activeId ? document.getElementById(activeId) : null;
        if (focus && !focus.closest('[inert]')) focus.focus({ preventScroll: true });
        else { const results = next.querySelector('.collection-results'); results.tabIndex = -1; results.focus({ preventScroll: true }); }
        next.querySelector('[data-status]').textContent = next.querySelector('.collection-toolbar > p').textContent;
      } catch (error) {
        if (error.name !== 'AbortError') this.showError();
      } finally { this.removeAttribute('aria-busy'); }
    }
    async loadMore(link) {
      if (this.hasAttribute('aria-busy')) return;
      this.setAttribute('aria-busy', 'true'); link.setAttribute('aria-disabled', 'true');
      const label = link.innerHTML; link.textContent = this.dataset.loading;
      try {
        const next = await this.fetchSection(link.href);
        const grid = this.querySelector('[data-product-grid]');
        const cards = [...next.querySelector('[data-product-grid]').children];
        const firstNew = cards[0]; grid.append(...cards);
        const pagination = this.querySelector('[data-pagination]');
        const offset = Number(pagination.dataset.offset);
        pagination.innerHTML = next.querySelector('[data-pagination]').innerHTML;
        const shown = offset + grid.children.length;
        pagination.querySelector('[data-shown-count]').textContent = (offset ? `${offset + 1}–` : '') + shown;
        this.querySelector('[data-status]').textContent = pagination.querySelector('p').textContent;
        firstNew?.querySelector('a')?.focus({ preventScroll: true });
        this.querySelector('.collection-error').hidden = true;
      } catch (error) {
        if (error.name !== 'AbortError') this.showError();
        link.innerHTML = label; link.removeAttribute('aria-disabled');
      } finally { this.removeAttribute('aria-busy'); }
    }
    showError() {
      const error = this.querySelector('.collection-error'); error.textContent = this.dataset.error; error.hidden = false;
      this.querySelector('[data-status]').textContent = '';
    }
  }
  if (!customElements.get('velvet-collection')) customElements.define('velvet-collection', VelvetCollection);
})();
